import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { ScopeCard } from "@/pages/dashboard/fs/ScopeCard";
import BidRoom from "@/pages/dashboard/fs/BidRoom";
import StartProjectWizard from "@/pages/dashboard/fs/wizards/StartProject";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Loader2, Plus, FileText, ArrowLeft, CheckCircle2, Ban, Clock, ExternalLink,
  ClipboardList, DollarSign, Send, ShieldAlert, Star,
} from "lucide-react";
import { V } from "@/lib/vocab";

// What a scope document can be. Attach almost anything a vendor might need to price the job;
// the AI can only READ the formats we can pull text out of.

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");
const fmt = (s) => (s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric" }) : "");

const STATUS_PILL = {
  open: ["Open", "bg-amber-100 text-amber-800"], awarded: ["Awarded", "bg-emerald-50 text-emerald-700"],
  cancelled: ["Cancelled", "bg-sand-100 text-ink/45"],
};

const PROP_PILL = {
  submitted: ["Awaiting decision", "bg-amber-100 text-amber-800"], approved: ["Approved", "bg-emerald-50 text-emerald-700"],
  hold: ["Second choice — on hold", "bg-indigo-50 text-indigo-700"],
  declined: ["Declined", "bg-red-50 text-red-700"], withdrawn: ["Withdrawn", "bg-sand-100 text-ink/45"],
  reprice: ["Scope changed — re-pricing", "bg-amber-50 text-amber-800"],
};

function ProposalCard({ tenantId, p, onChanged, canDecide, projectId, onAwarded, threshold, awardPending, canBoardSign }) {
  const [signing, setSigning] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const pill = PROP_PILL[p.status] || PROP_PILL.submitted;
  // Derived, not remembered: an award waiting on the board survives a refresh and looks the
  // same to the manager who asked and to the board member who has to sign it.
  const overThreshold = !!Number(threshold) && (Number(p.total) || 0) >= Number(threshold);
  const waitingOnBoard = (awardPending || {}).proposal_id === p.id;

  const award = async (withSignature) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/projects/${projectId}/award`,
        withSignature ? { proposal_id: p.id, signed_by: name, agreed: true } : { proposal_id: p.id });
      if (data.needs_board) {
        toast.info(data.can_sign
          ? `That's over the ${V.grp()} threshold — sign it below to award it`
          : `That's over the ${V.grp()} threshold — ${(data.signers || []).map((s) => s.name).join(" and ") || `the ${V.grp()}`} has been asked to sign it off`);
        onChanged?.();
      } else {
        toast.success(`Awarded${data.runner_up ? ` · ${data.runner_up} held as second choice` : ""} — run the kickoff next`);
        onAwarded?.(data);
        onChanged?.();
      }
    } catch (e) { toast.error(errMsg(e, "Could not award it")); }
    setBusy(false);
  };

  const approve = async () => {
    if (projectId) {
      if (name.trim().length < 3 || !agree) return toast.error("Type your name and confirm to sign");
      return award(true);
    }
    if (name.trim().length < 3 || !agree) return toast.error("Type your name and confirm to sign");
    setBusy(true);
    try { await api.post(`/tenants/${tenantId}/field-services/proposals/${p.id}/approve`, { agreed: true, signed_by: name }); toast.success("Approved & signed"); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "Could not approve")); }
    setBusy(false);
  };
  const decline = async () => {
    setBusy(true);
    try { await api.post(`/tenants/${tenantId}/field-services/proposals/${p.id}/decline`, { reason }); toast.success("Declined"); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "Could not decline")); }
    setBusy(false);
  };
  const remindDocs = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/vendors/${p.vendor_id}/remind-docs`);
      toast.success(data.sent ? "Asked — we've texted and emailed their office"
        : "Nobody routable in their office yet — chase them yourself");
    } catch (e) { toast.error(errMsg(e, "Could not send it")); }
    setBusy(false);
  };

  return (
    <div data-testid={`proposal-${p.id}`} className="rounded-2xl border border-sand-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-ink">{p.vendor_name}</div>
          <div className="text-xs text-ink/45">{p.title} · submitted {fmt(p.submitted_at)}{p.submitted_by ? ` by ${p.submitted_by}` : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${pill[1]}`}>{pill[0]}</span>
          {p.paperwork && !p.paperwork.ok && (
            <span data-testid={`proposal-paper-chip-${p.id}`}
              className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
              Paperwork outstanding
            </span>
          )}
          <span className="text-base font-extrabold text-ink">{money(p.total)}</span>
        </div>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink/70">{p.scope}</p>
      {p.pricing_type === "line_items" && (p.line_items || []).length > 0 && (
        <div className="mt-2 rounded-xl bg-sand-50 p-3 text-sm">
          {p.line_items.map((li, i) => (
            <div key={i} className="flex justify-between py-0.5"><span className="text-ink/60">{li.desc}</span><span className="font-semibold text-ink/80">{money(li.amount)}</span></div>
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink/50">
        {p.start_date && <span>Start: {p.start_date}</span>}
        {p.duration && <span>Duration: {p.duration}</span>}
      </div>
      {(p.attachments || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {p.attachments.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noreferrer" data-testid={`proposal-attach-${p.id}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 px-2.5 py-1 text-xs font-semibold text-ink/70 hover:border-ink/30">
              <FileText className="h-3.5 w-3.5" /> {a.name || "Attachment"} <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      )}
      {p.paperwork && !p.paperwork.ok && (
        <div className="mt-3 flex flex-wrap items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-900"
          data-testid={`proposal-paperwork-${p.id}`}>
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1">
            Read it, compare it, shortlist it — but they can't be put on the property until we have{" "}
            {(p.paperwork.missing || []).map((m) => m.label.toLowerCase()).join(" and ")}. We're asking
            them for it on a schedule; award them anyway and the job waits until it lands.
          </span>
          {canDecide && (
            <button data-testid={`proposal-remind-${p.id}`} disabled={busy} onClick={remindDocs}
              className="w-full sm:w-auto shrink-0 rounded-lg border border-amber-400 bg-white px-2.5 py-1 text-xs font-bold text-amber-900 disabled:opacity-50">
              Ask them now
            </button>
          )}
        </div>
      )}
      {p.rating?.count > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-ink/50">
          <Star className="h-3.5 w-3.5 text-amber-500" /> {p.rating.avg} across {p.rating.count} job(s)
        </div>
      )}
      {p.status === "hold" && (
        <div className="mt-3 rounded-xl bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-800">
          Held as second choice — if the winner's paperwork never lands you can move to them without re-bidding.
        </div>
      )}
      {p.status === "approved" && p.signature && (
        <div className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid={`proposal-signed-${p.id}`}>
          Signed by {p.signature.name} on {fmt(p.signature.signed_at)}
        </div>
      )}
      {p.status === "declined" && p.decline_reason && (
        <div className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">Declined — {p.decline_reason}</div>
      )}
      {["submitted", "hold"].includes(p.status) && canDecide && (
        <div className="mt-3">
          {!signing && !declining && !waitingOnBoard && (
            <div className="flex flex-wrap gap-2">
              <button data-testid={`proposal-approve-${p.id}`}
                onClick={() => (projectId ? award(false) : setSigning(true))} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {projectId ? (overThreshold ? `Award — needs ${V.aGrp()} signature` : "Award this job") : "Approve & sign"}
              </button>
              <button data-testid={`proposal-decline-${p.id}`} onClick={() => setDeclining(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-50">
                <Ban className="h-4 w-4" /> Decline
              </button>
            </div>
          )}
          {waitingOnBoard && (
            <div className="space-y-2 rounded-xl border border-indigo-200 bg-indigo-50 p-3" data-testid={`proposal-board-${p.id}`}>
              <p className="text-xs leading-relaxed text-indigo-900">
                {money(p.total)} is at or above this {V.lc("org")}'s {money(threshold)} threshold, so {V.aGrp()}
                member has to sign this award off before the contractor is told anything. They've been
                emailed.
              </p>
              {canBoardSign ? (
                <>
                  <input data-testid={`proposal-board-name-${p.id}`} value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Type your full name to sign"
                    className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm outline-none" />
                  <button data-testid={`proposal-board-sign-${p.id}`} onClick={() => award(true)}
                    disabled={busy || name.trim().length < 3}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign the award
                  </button>
                </>
              ) : (
                <p className="text-xs font-bold text-indigo-900" data-testid={`proposal-board-waiting-${p.id}`}>
                  Waiting on {V.aGrp()} signature.
                </p>
              )}
            </div>
          )}
          {signing && (
            <div className="space-y-2 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
              <input data-testid={`proposal-sign-name-${p.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name to sign"
                className="w-full rounded-lg border border-sand-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30" />
              <button data-testid={`proposal-agree-${p.id}`} onClick={() => setAgree(!agree)}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold ${agree ? "bg-emerald-600 text-white" : "bg-white text-ink/60"}`}>
                <CheckCircle2 className="h-4 w-4" /> I approve this proposal on behalf of the {V.lc("org")}.
              </button>
              <div className="flex gap-2">
                <button data-testid={`proposal-sign-confirm-${p.id}`} onClick={approve} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign & approve
                </button>
                <button onClick={() => { setSigning(false); setAgree(false); setName(""); }} className="rounded-lg border border-sand-300 px-3 py-2 text-sm font-semibold text-ink/60">Cancel</button>
              </div>
            </div>
          )}
          {declining && (
            <div className="flex items-center gap-2">
              <input data-testid={`proposal-decline-reason-${p.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
                className="flex-1 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-500/30" />
              <button data-testid={`proposal-decline-confirm-${p.id}`} onClick={decline} disabled={busy}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Confirm</button>
              <button onClick={() => setDeclining(false)} className="rounded-lg border border-sand-300 px-3 py-2 text-sm font-semibold text-ink/60">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function FieldProjects({ tenantId, vendors, readOnly, canDecide, canAuthor, canBoardSign, autoOpenNew, onAutoOpened, onAwarded }) {
  // The board commissions work too: they can post a project and author its scope. Everything
  // else on the Field Services screens stays manager-only.
  const mayAuthor = canAuthor ?? !readOnly;
  const [d, setD] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tenants/${tenantId}/field-services/projects`);
      setD(data);
    } catch (e) { toast.error(errMsg(e, "Could not load projects")); setD({ projects: [], awaiting_review: 0 }); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (autoOpenNew) { setModalOpen(true); onAutoOpened?.(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);

  const openProject = async (pid) => {
    setOpenId(pid); setDetail(null);
    try { const { data } = await api.get(`/tenants/${tenantId}/field-services/projects/${pid}`); setDetail(data); }
    catch (e) { toast.error(errMsg(e, "Could not open")); }
  };
  const refreshDetail = async () => { await openProject(openId); await load(); };

  if (!d) return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;

  if (openId) {
    const proj = detail?.project;
    const todayStr = new Date().toISOString().slice(0, 10);
    const scopeLocked = !proj || proj.status !== "open"
      || (!!proj.bids_due && proj.bids_due.slice(0, 10) < todayStr);
    const openBids = (detail?.proposals || []).filter((p) => p.status === "submitted").length;
    return (
      <div className="space-y-4" data-testid="project-detail">
        <button data-testid="project-back" onClick={() => { setOpenId(null); setDetail(null); }}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-ink/60 hover:text-ink"><ArrowLeft className="h-4 w-4" /> All projects</button>
        {!detail ? <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div> : (
          <>
            <div className="rounded-2xl border border-sand-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-heading text-lg font-bold text-ink">{proj.title}</div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${(STATUS_PILL[proj.status] || STATUS_PILL.open)[1]}`}>{(STATUS_PILL[proj.status] || STATUS_PILL.open)[0]}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink/50">
                <span className="font-semibold uppercase tracking-wide">{proj.mode === "rfp" ? "Open RFP" : "Direct request"}</span>
                {proj.created_by?.name && (
                  <span data-testid="project-posted-by">
                    Posted by {proj.created_by.name}{proj.created_by.role === "board_member" ? ` (${V.grp()})` : ""}
                  </span>
                )}
                {proj.category && <span>{CAT_LABEL[proj.category] || proj.category}</span>}
                {proj.budget != null && <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />Budget {money(proj.budget)}</span>}
                {(proj.notified_vendor_ids || []).length > 0 && (
                  <span className="inline-flex items-center gap-1" data-testid="project-sent-count">
                    <Send className="h-3.5 w-3.5" />Sent to {proj.notified_vendor_ids.length}
                  </span>
                )}
                {proj.bids_due && (
                  <span className="inline-flex items-center gap-1" data-testid="project-bids-due">
                    <Clock className="h-3.5 w-3.5" />
                    Bids {proj.bids_due.slice(0, 10) < new Date().toISOString().slice(0, 10) ? "closed" : "due"} {proj.bids_due.slice(0, 10)}
                  </span>
                )}
              </div>
              {proj.audience && (
                <div className="mt-2 rounded-lg bg-sand-50 px-3 py-2 text-xs text-ink/55" data-testid="project-audience">
                  Asked {proj.audience.resolved} compan{proj.audience.resolved === 1 ? "y" : "ies"}
                  {(proj.audience.trades || []).length ? ` in ${proj.audience.trades.map((t) => CAT_LABEL[t] || t).join(", ")}` : ""}
                  {proj.audience.area_only && proj.audience.market ? ` serving ${proj.audience.market}` : " anywhere"}
                  {proj.audience.insured_only ? " · insurance current" : ""}
                  {proj.audience.min_rating ? ` · ${proj.audience.min_rating}★ and up` : ""}
                </div>
              )}
              {proj.description && <p className="mt-2 text-sm text-ink/70">{proj.description}</p>}
            </div>

            <BidRoom tenantId={tenantId} detail={detail} onChanged={refreshDetail} mayManage={!readOnly} />

            {/* Scope of work + documents — what the vendors bid against. Versions, not overwrites;
                a change after bids landed resets them and tells every bidder (iter372). */}
            <ScopeCard tenantId={tenantId} kind="project" id={openId} doc={proj} testid="project-scope"
              canAuthor={mayAuthor} onChanged={refreshDetail} openBids={openBids}
              draftCtx={{ title: proj.title, category: proj.category, budget: proj.budget, bids_due: proj.bids_due, notes: proj.description, project_id: proj.id }}
              locked={!scopeLocked ? "" : proj.status === "awarded" ? "Awarded — the scope is locked so it matches what was signed."
                : proj.status === "cancelled" ? "Cancelled — the scope is locked."
                  : `Bids closed ${(proj.bids_due || "").slice(0, 10)} — the scope is locked so nobody's price changes after the fact.`} />
            <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Proposals ({(detail.proposals || []).length})</div>
            {(detail.proposals || []).length === 0 && <div className="rounded-xl border border-dashed border-sand-300 py-8 text-center text-sm text-ink/45">No proposals yet.</div>}
            {(detail.proposals || []).map((p) => (
              <ProposalCard key={p.id} tenantId={tenantId} p={p} onChanged={refreshDetail}
                canDecide={canDecide ?? !readOnly} projectId={proj.id}
                threshold={(detail.settings || {}).board_threshold}
                awardPending={proj.award_pending} canBoardSign={canBoardSign}
                onAwarded={() => { setOpenId(null); setDetail(null); onAwarded?.(); }} />
            ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="projects-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold text-ink/45">{d.projects.length} project{d.projects.length === 1 ? "" : "s"}</div>
        {mayAuthor && (
          <button data-testid="new-project-btn" onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-redline-500 px-3.5 py-2 text-sm font-bold text-white hover:bg-redline-600">
            <Plus className="h-4 w-4" /> New project
          </button>
        )}
      </div>
      {d.awaiting_review > 0 && (
        <div data-testid="proposals-awaiting" className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
          <span className="font-bold">{d.awaiting_review}</span> proposal{d.awaiting_review === 1 ? "" : "s"} awaiting your review
        </div>
      )}
      {d.projects.length === 0 && (
        <div className="rounded-xl border border-dashed border-sand-300 py-14 text-center text-sm text-ink/45" data-testid="projects-empty">
          No projects yet. Create one to request a proposal from a vendor, or post an RFP for bids.
        </div>
      )}
      {d.projects.map((p) => (
        <button key={p.id} data-testid={`project-row-${p.id}`} onClick={() => openProject(p.id)}
          className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-sand-200 bg-white px-4 py-3 text-left transition-colors hover:border-ink/25">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 shrink-0 text-ink/40" />
              <span className="truncate text-sm font-bold text-ink">{p.title}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-ink/60">
              <span className="uppercase tracking-wide">{p.mode === "rfp" ? "RFP" : "Direct"}</span>
              {p.category && <span>· {CAT_LABEL[p.category] || p.category}</span>}
              {(p.scope || (p.scope_files || []).length > 0) && (
                <span data-testid={`project-has-scope-${p.id}`} className="inline-flex items-center gap-1">
                  · <FileText className="h-3 w-3" />Scope{(p.scope_files || []).length ? ` +${p.scope_files.length}` : ""}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold">
            {p.proposals?.submitted > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800"><Clock className="h-3 w-3" />{p.proposals.submitted} to review</span>}
            <span className="text-ink/45">{p.proposals?.total || 0} proposal{(p.proposals?.total || 0) === 1 ? "" : "s"}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${(STATUS_PILL[p.status] || STATUS_PILL.open)[1]}`}>{(STATUS_PILL[p.status] || STATUS_PILL.open)[0]}</span>
          </div>
        </button>
      ))}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
            <DialogDescription>Ask one vendor directly, or put it out for bids to the right companies in the network.</DialogDescription>
          </DialogHeader>
          <StartProjectWizard tenantId={tenantId} vendors={vendors} onDone={load} onClose={() => setModalOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
